export type TimeframeKey =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'ytd'
  | 'last_12_months'
  | 'all_time'
  | 'custom';

export const TIMEFRAME_OPTIONS: Array<{ value: TimeframeKey; label: string }> = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'last_12_months', label: 'Last 12 months' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

export interface CustomRange {
  /** YYYY-MM-DD, as a date input yields it. Empty = open-ended. */
  from: string;
  to: string;
}

/** Half-open range in local time: from <= t < to. Null bound = unbounded. */
export interface DateRange {
  from: Date | null;
  to: Date | null;
}

const startOfMonth = (y: number, m: number) => new Date(y, m, 1);
const localDay = (ymd: string) => (ymd ? new Date(`${ymd}T00:00:00`) : null);
const nextDay = (d: Date | null) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1) : null);

export function timeframeRange(key: TimeframeKey, custom: CustomRange, now = new Date()): DateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3) * 3;
  switch (key) {
    case 'this_month':
      return { from: startOfMonth(y, m), to: startOfMonth(y, m + 1) };
    case 'last_month':
      return { from: startOfMonth(y, m - 1), to: startOfMonth(y, m) };
    case 'this_quarter':
      return { from: startOfMonth(y, q), to: startOfMonth(y, q + 3) };
    case 'last_quarter':
      return { from: startOfMonth(y, q - 3), to: startOfMonth(y, q) };
    case 'ytd':
      return { from: startOfMonth(y, 0), to: null };
    case 'last_12_months':
      return { from: startOfMonth(y, m - 11), to: null };
    case 'all_time':
      return { from: null, to: null };
    case 'custom':
      return { from: localDay(custom.from), to: nextDay(localDay(custom.to)) };
  }
}

export function inDateRange(iso: string | null | undefined, range: DateRange): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  if (range.from && t < range.from.getTime()) return false;
  if (range.to && t >= range.to.getTime()) return false;
  return true;
}
